import MiniCartAccessories from "@NA/CustomComponents/MiniCartAccessories";
import MiniCartLine from "@NA/CustomComponents/MiniCartLine";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { Typography } from "@insite/mobius/index";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonPresentationProps, ButtonWrapper } from "@insite/mobius/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { IconWrapper } from "@insite/mobius/Icon";

interface OwnProps {
    isModalOpen: boolean;
    onModalClose: () => void;
    cartLine: CartLineModel;
    extendedStyles?: MiniCartModalStyles;
    quantityOrdered?: number;
}

const mapStateToProps = (state: ApplicationState) => {
    const currentPage = getCurrentPage(state);
    return {
        cart: getCurrentCartState(state).value,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        currentPage,
        isPlp: currentPage.type === "ProductListPage",
        isPdp: currentPage.type === "ProductDetailsPage",
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface MiniCartModalStyles {
    modal?: ModalPresentationProps;
    maxModalView?: ModalPresentationProps;
    viewCartShoppingWrapper?: InjectableCss;
    viewCartButton?: ButtonPresentationProps;
    continueShoppingButton?: ButtonPresentationProps;
}

export const miniCartModalStyles: MiniCartModalStyles = {
    modal: {
        cssOverrides: {
            scrim: css`
                z-index: 9999;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 0;
                > ${ButtonWrapper} {
                    padding: 0;
                    border: 0;
                    &:hover{
                        background: none;
                    }
                    > ${IconWrapper} {
                        width: 35px;
                        height: 35px;
                        top: -7px;
                        > svg {
                            width: 100%;
                            stroke-width: 1.3;
                            height: 100%;
                            color: #006ae3;
                        }
                    }
                }
            `,
            modalContent: css`
                padding: 0;
                overflow: hidden;
                scrollbar-width: thin;
                scrollbar-color: #7c93ad #ececec;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            max-height: 67vh;
                            overflow: auto;
                            overflow-x: hidden;                       
                        `,
                        css`
                            max-height: 67vh;
                            overflow: auto;
                            overflow-x: hidden;                         
                        `,
                        css`
                            max-height: 80vh;
                            overflow: auto;
                            overflow-x: hidden; 
                        `,
                        null,
                        null,
                    ])}
            `,
            modalBody: css`
                border-radius: 6px;
                box-shadow: 0 1px 9px 0 rgba(0, 0, 0, 0.14);
                padding: 28px 20px 33px 24px;
                border: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            max-width: 98% !important;   
                            min-width: calc(100% - 20px);
                            width: calc(100% - 20px);
                            height: auto;
                            max-height: 80%; 
                            padding: 15px;                      
                        `,
                        css`
                            max-width: 95% !important; 
                            height: auto;
                            max-height: 80%;   
                            padding: 15px;                         
                        `,
                        css`
                            max-width: 80% !important;  
                            height: auto;
                            max-height: 80%; 
                            padding: 15px;
                        `,
                        css`
                            max-width: 1185px !important;
                        `,
                        css`
                            max-width: 1185px !important;
                        `,
                    ])}
            `,
            headlineTypography: css`
                font-size: 32px;
                margin-bottom: 0;
                line-height:normal;
            `,
        },
    },
    maxModalView: {
        cssOverrides: {
            scrim: css`
                z-index: 9999;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 0;
                > ${ButtonWrapper} {
                    padding: 0;
                    border: 0;
                    &:hover{
                        background: none;
                    }
                    > ${IconWrapper} {
                        width: 35px;
                        height: 35px;
                        top: -7px;
                        > svg {
                            width: 100%;
                            stroke-width: 1.3;
                            height: 100%;
                            color: #006ae3;
                        }
                    }
                }
            `,
            modalContent: css`
                padding: 0;
            `,
            modalBody: css`
                border-radius: 6px;
                box-shadow: 0 1px 9px 0 rgba(0, 0, 0, 0.14);
                padding: 28px 20px 33px 24px;
                border: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            max-width: 95% !important;
                            height: 95% !important;
                            padding: 28px 15px 33px 10px;
                        `,
                        css`
                            max-width: 95% !important;
                            height: 80% !important;
                            padding: 28px 15px 33px 10px;
                        `,
                        css`
                            max-width: 70% !important;
                            height: 80% !important;
                            padding: 28px 15px 33px 10px;
                        `,
                        css`
                            max-width: 1185px !important;
                        `,
                        css`
                            max-width: 1185px !important;
                        `,
                    ])}
            `,
            headlineTypography: css`
                font-size: 32px;
                margin-bottom: 0;
                line-height:normal;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                font-size: 22px;
                            `,
                        ],
                        "max",
                    );
                }}
            `,
        },
    },
    viewCartShoppingWrapper: {
        css: css`
            padding: 20px 20px 18px 0;
            justify-content: flex-end;
            display: flex;
            border-bottom: 1px solid ${getColor("common.border")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 0px;
                        padding-right: 0;
                        padding-left: 0;
                        justify-content: center;
                    `,
                    css`
                        margin-top: 20px;
                        padding-right: 0;
                        padding-left: 0;
                    `,
                    css`
                        margin-top: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    viewCartButton: {
        css: css`
            margin-right: 20px;
            white-space: nowrap;
            border: solid 1px #004da6;
            background-color: #fff;
            color: #004da6;
            border-radius: 4px;
            min-width: 108px;
            padding: 5px 10px;
            &:hover{
                background-color: #c8ddf6;
            }
        `,
    },
    continueShoppingButton: {
        css: css`
            white-space: nowrap;
            border-radius: 4px;
            min-width: 182px;
            padding: 5px 10px;
        `,
    },
};

const MiniCartModal = ({
    isModalOpen,
    onModalClose,
    history,
    cartLine,
    cartUrl,
    extendedStyles,
    cart,
    currentPage,
    quantityOrdered,
    isPdp,
    isPlp,
}: Props) => {
    const [styles] = useState(() => mergeToNew(miniCartModalStyles, extendedStyles));
    const [showModalMaxHeight, setShowModalMaxHeight] = useState<boolean>(false);
    const isCartPage = currentPage.type === "CartPage" || currentPage.type === "MyListsPage";

    if ((!cartLine || !cart) || (!isPdp && !isPlp)) {
        return null;
    } 

    const onClick = (link: string | undefined) => {
        if (link) {
            history.push(link);
        }
    };

    const handleModalHeight = (arg: boolean) => {
        setShowModalMaxHeight(arg);
    };

    return (
        <Modal
            {...(showModalMaxHeight ? styles.maxModalView : styles.modal)}
            headline={translate("Added to Cart")}
            isOpen={isModalOpen && !isCartPage}
            handleClose={onModalClose}
            data-test-selector="cart_miniCartModal"
        >
            <MiniCartLine cartLine={cartLine} quantityOrdered={quantityOrdered} onModalClose={onModalClose}/>
            <StyledWrapper {...styles.viewCartShoppingWrapper}>
                <Button variant="secondary" {...styles.viewCartButton} onClick={() => onClick(cartUrl)}>
                    {translate("View Cart")}
                </Button>
                <Button variant="primary" {...styles.continueShoppingButton} onClick={onModalClose}>
                    {translate("Continue Shopping")}
                </Button>
            </StyledWrapper>
            <MiniCartAccessories handleModalHeight={handleModalHeight} cartLine={cartLine} />
        </Modal>
    );
};

export default connect(mapStateToProps)(withHistory(MiniCartModal));
